import { useCallback, useEffect, useRef, useState } from 'react';
import soundUrl from '../../app/audio/new_message.mp3';
import iconUrl from '../../app/images/notification-fly-large.png';
import { useWindowSession } from '../app/WindowSession';
import { readNotificationSound, useNotificationSound } from './notificationSound';

export interface NotificationReport { sound: string; popup: string; error: string }
type Permission = NotificationPermission | 'unsupported';
export function useChatNotifications(userId?: number) {
  const { isPrimary, workspace } = useWindowSession();
  const sound = useNotificationSound(userId);
  const audio = useRef<HTMLAudioElement | null>(null);
  const primed = useRef(false);
  const priming = useRef<Promise<void> | null>(null);
  const heard = useRef(new Set<string>());
  const [permission, setPermission] = useState<Permission>(() => 'Notification' in window ? Notification.permission : 'unsupported');
  const [report, setReport] = useState<NotificationReport>({ sound: 'Нужна проверка звука', popup: 'Нужна проверка попапа', error: '' });
  useEffect(() => {
    const player = new Audio(soundUrl); player.preload = 'auto'; audio.current = player; primed.current = false; heard.current.clear();
    const unlock = () => {
      if (primed.current || priming.current || !player.paused) return;
      player.volume = 0;
      priming.current = player.play().then(() => { player.pause(); player.currentTime = 0; primed.current = true; }).catch(() => { /* Retry on the next gesture, not just once. */ }).finally(() => { const saved=readNotificationSound(userId);player.volume=saved.muted?0:saved.volume; priming.current = null; });
    };
    const sync = () => setPermission('Notification' in window ? Notification.permission : 'unsupported');
    document.addEventListener('pointerdown', unlock); document.addEventListener('keydown', unlock);
    window.addEventListener('focus', sync);
    return () => { document.removeEventListener('pointerdown', unlock); document.removeEventListener('keydown', unlock); window.removeEventListener('focus', sync); player.pause(); audio.current = null; };
  }, [userId]);
  useEffect(() => {
    const player=audio.current;if(!player)return;
    if(sound.muted)player.pause();
    if(!priming.current)player.volume=sound.muted?0:sound.volume;
  },[sound.volume,sound.muted]);
  const testSound = useCallback(async () => {
    if (workspace || !isPrimary()) return;
    try {
      // A permission-unlock playback must finish before the audible playback;
      // otherwise its pause() may silence the actual incoming-message sound.
      await priming.current;
      if (!isPrimary()) return;
      const saved=readNotificationSound(userId);
      if(saved.muted) { setReport(current=>({...current,sound:'Звук новых сообщений выключен.',error:''}));return; }
      const player = audio.current;
      if (!player) throw new Error('Звук ещё загружается. Повторите проверку.');
      player.volume = saved.volume; player.currentTime = 0; await player.play(); primed.current = true;
      setReport(current => ({ ...current, sound: 'Браузер воспроизвёл сигнал. Подтвердите, что его слышно.', error: '' }));
    } catch {
      setReport(current => ({ ...current, sound: 'Браузер не разрешил или не смог воспроизвести звук.', error: 'Нажмите «Проверить звук». Проверьте громкость и звук вкладки.' }));
    }
  }, [workspace, isPrimary, userId]);
  const enable = useCallback(async () => {
    if (!('Notification' in window) || !window.isSecureContext) { setPermission('unsupported'); setReport(current => ({ ...current, error: 'Системные уведомления требуют HTTPS и поддержки браузера. Откройте skrepka.myxa.tv по HTTPS.' })); return; }
    try { const value = await Notification.requestPermission(); setPermission(value); if (value !== 'granted') setReport(current => ({ ...current, error: 'Разрешите уведомления в настройках сайта у адресной строки.' })); }
    catch { setReport(current => ({ ...current, error: 'Браузер не позволил запросить разрешение на уведомления.' })); }
  }, []);
  const showPopup = useCallback((title: string, body: string, dialogId?: string, tag?: string) => {
    if (workspace || !isPrimary()) return;
    try {
      if (!('Notification' in window)) throw new Error('Системные уведомления недоступны в этом браузере.');
      setPermission(Notification.permission);
      if (Notification.permission !== 'granted') throw new Error('Для попапов нужно разрешить уведомления этому сайту.');
      const popup = new Notification(title, { body, icon: iconUrl, tag: tag || 'skrepka-notification-test', silent: true });
      popup.onclick = () => { window.focus(); if (dialogId) window.location.assign(`/dialogs?id=${encodeURIComponent(dialogId)}`); popup.close(); };
      popup.onerror = () => setReport(current => ({ ...current, popup: 'Браузер сообщил об ошибке показа.', error: 'Не удалось показать системное уведомление.' }));
      setReport(current => ({ ...current, popup: 'Запрос попапа передан системе. Подтвердите, что он появился.', error: '' }));
    } catch (error) { setReport(current => ({ ...current, popup: error instanceof Error ? error.message : 'Ошибка попапа', error: error instanceof Error ? error.message : 'Ошибка попапа' })); }
  }, [workspace, isPrimary]);
  const testPopup = useCallback(() => showPopup('Скрепка · проверка уведомлений', 'Если вы видите этот баннер, подтвердите это в окне проверки.'), [showPopup]);
  const notify = useCallback(async (id: string, title: string, body: string, dialogId?: string) => {
    if (!userId || !isPrimary() || heard.current.has(id)) return;
    heard.current.add(id); if (heard.current.size > 500) heard.current.delete(heard.current.values().next().value!);
    const claimAndNotify = () => {
      if (!isPrimary()) return;
      const key = `skrepka.notifications.seen.${userId}`;
      try {
        const saved: unknown = JSON.parse(localStorage.getItem(key) || '[]');
        const ids = Array.isArray(saved) ? saved.filter((item): item is string => typeof item === 'string') : [];
        if (ids.includes(id)) return;
        localStorage.setItem(key, JSON.stringify([...ids.slice(-255), id]));
      } catch { /* Still notify when storage is disabled; per-tab dedupe remains. */ }
      // The app may be focused on tasks or another conversation. Do not
      // suppress all desktop notifications simply because it has focus.
      void testSound(); showPopup(title, body, dialogId, `skrepka-message-${id}`);
    };
    try {
      if (navigator.locks) await navigator.locks.request(`skrepka-notifier-${userId}`, claimAndNotify);
      else claimAndNotify();
    } catch { claimAndNotify(); }
  }, [showPopup, testSound, userId, isPrimary]);
  return { permission, enable, report, testSound, testPopup, notify, sound };
}
